import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { useAppContext } from 'providers/AppProvider';
import { tooltipFormatterDefault } from 'helpers/echart-utils';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useMemo } from 'react';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer,
  LegendComponent
]);

interface ChartData {
  [key: string]: { blocked: number; qualified: number };
}

interface DepartmentwiseQualifiationProps {
  data: ChartData | null;
  loading: boolean;
}

const DepartmentwiseQualifiation = ({
  data,
  loading
}: DepartmentwiseQualifiationProps) => {
  const { getThemeColor } = useAppContext();

  const chartData = useMemo(() => {
    if (data) {
      const keys = Object.keys(data);
      const passedValue = keys.map(info => data[info].qualified);
      const blockedValue = keys.map(info => data[info].blocked);
      const allZero =
        passedValue.every(v => v === 0) && blockedValue.every(v => v === 0);

      return allZero ? null : { keys, passedValue, blockedValue };
    }
    return null;
  }, [data]);

  if (loading) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ height: '320px' }}
      >
        <div className="bar-chart-skeleton">
          <div className="bar" style={{ height: '30%' }}></div>
          <div className="bar" style={{ height: '50%' }}></div>
          <div className="bar" style={{ height: '80%' }}></div>
          <div className="bar" style={{ height: '40%' }}></div>
          <div className="bar" style={{ height: '80%' }}></div>
          <div className="bar" style={{ height: '60%' }}></div>
          <div className="bar" style={{ height: '40%' }}></div>
        </div>
      </div>
    );
  }

  if (!chartData) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ height: '320px' }}
      >
        <p className="mt-2 text-muted">No Records</p>
      </div>
    );
  }

  const getDefaultOptions = (getThemeColor: (name: string) => string) => ({
    color: [getThemeColor('primary'), getThemeColor('tertiary-bg')],
    tooltip: {
      trigger: 'axis',
      formatter: tooltipFormatterDefault
    },
    legend: {
      data: ['Qualified', 'Blocked'],
      right: 'right',
      width: '100%',
      itemWidth: 16,
      itemHeight: 8,
      itemGap: 20,
      top: 3,
      inactiveColor: getThemeColor('quaternary-color'),
      textStyle: {
        color: getThemeColor('body-color'),
        fontWeight: 600,
        fontFamily: 'Nunito Sans'
      }
    },
    xAxis: {
      type: 'category',
      axisLabel: {
        color: getThemeColor('secondary-color'),
        fontFamily: 'Nunito Sans',
        fontWeight: 600,
        fontSize: 12.8
      },
      data: chartData.keys,
      axisLine: {
        lineStyle: {
          color: getThemeColor('tertiary-bg')
        }
      },
      axisTick: false
    },
    yAxis: {
      axisPointer: { type: 'none' },
      axisTick: 'none',
      splitLine: {
        interval: 5,
        lineStyle: {
          color: getThemeColor('secondary-bg')
        }
      },
      axisLine: { show: false },
      axisLabel: {
        fontFamily: 'Nunito Sans',
        fontWeight: 600,
        fontSize: 12.8,
        color: getThemeColor('secondary-color'),
        margin: 20,
        verticalAlign: 'bottom'
      }
    },
    series: [
      {
        name: 'Qualified',
        type: 'bar',
        barWidth: '20px',
        data: chartData.passedValue,
        barGap: '30%',
        label: { show: false },
        itemStyle: {
          borderRadius: [2, 2, 0, 0],
          color: getThemeColor('primary')
        }
      },
      {
        name: 'Blocked',
        type: 'bar',
        data: chartData.blockedValue,
        barWidth: '20px',
        barGap: '30%',
        label: { show: false },
        z: 10,
        itemStyle: {
          borderRadius: [2, 2, 0, 0],
          color: getThemeColor('info-bg-subtle')
        }
      }
    ],
    grid: {
      right: 0,
      left: 3,
      bottom: 0,
      top: '15%',
      containLabel: true
    },
    animation: false
  });

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={getDefaultOptions(getThemeColor)}
      style={{ minHeight: '320px', width: '100%' }}
    />
  );
};

export default DepartmentwiseQualifiation;
